import { Box, Divider, Paper, Stack, Typography, useTheme } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { useEffect, useState } from "react";

import { AddressSection } from "./ProfileV2/AddressSection";
import { BasicsSection } from "./ProfileV2/BasicsSection";
import { FacilityContactsSection } from "./ProfileV2/FacilityContactsSection";
import { FacilityDocumentsSection } from "./ProfileV2/FacilityDocumentsSection";
import { FacilityHeaderV2, SECTION_NAV_ID } from "./ProfileV2/FacilityHeaderV2";
import { LocalAgenciesSection } from "./ProfileV2/LocalAgenciesSection";
import { QuickLinks } from "./ProfileV2/QuickLinks";
import { StateInformationSection } from "./ProfileV2/StateInformationSection";
import { TasksSnapshot } from "./ProfileV2/TasksSnapshot";
import { useFacility } from "./useFacility";

export const FacilityProfileV2: React.FC = () => {
  const theme = useTheme();
  const { tenant } = useTenant();
  const { data, loading, error } = useFacility();
  const [navBottom, setNavBottom] = useState(0);

  // Dynamically measure the bottom edge of the sticky header (tabs + section chips)
  // so the Quick Links sidebar can stick just below it. The header height varies
  // depending on whether the section chips wrap to multiple lines, so we use a
  // ResizeObserver to recalculate whenever it changes (e.g. on window resize).
  useEffect(() => {
    const nav = document.getElementById(SECTION_NAV_ID);
    if (!nav) return;

    const updateHeight = () => {
      const rect = nav.getBoundingClientRect();
      setNavBottom(rect.bottom);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [loading]);

  useBreadcrumb([
    {
      label: "Facilities",
      to: `/o/${tenant?.id}/facilities`,
    },
    {
      label: data?.facility?.name ?? "",
    },
  ]);

  if (error) {
    throw error;
  }

  if (loading) {
    return (
      <Box p={theme.spacing(3)}>
        <Typography variant="h6">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ background: blueGrey[theme.palette.mode === "light" ? 50 : 900] }}>
      <FacilityHeaderV2 />

      <Stack direction="row" spacing={2} sx={{ m: 2 }}>
        <Paper elevation={0} sx={{ p: 3, flex: 1, minWidth: 0 }}>
          <Typography variant="h5" mb={5}>
            Facility Overview
          </Typography>
          <BasicsSection />
          <Divider sx={{ my: 5 }} />
          <AddressSection />
          <Divider sx={{ my: 5 }} />
          <LocalAgenciesSection />
          <Divider sx={{ my: 5 }} />
          <StateInformationSection />
          <Divider sx={{ my: 5 }} />
          <FacilityContactsSection />
          <Divider sx={{ my: 5 }} />
          <FacilityDocumentsSection />
        </Paper>
        <Stack
          spacing={2}
          sx={{
            alignSelf: "flex-start",
            position: "sticky",
            display: { xs: "none", md: "flex" },
            top: navBottom + 12,
          }}
        >
          <Paper elevation={0} sx={{ p: 2 }}>
            <TasksSnapshot />
          </Paper>
          <Paper elevation={0} sx={{ p: 2 }}>
            <QuickLinks />
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};
